<script>
/* global T */
import componentMixin from "../mixins/component-mixin";

export default {
    name: "TdtSlotMarker",
    mixins: [componentMixin],
    props: {
        position: {
            type: Array,
            default: () => []
        },
        extData: {
            type: Object,
            default: () => ({})
        },
        anchor: {
            type: String,
            default: "bottom-center",
            validator(val) {
                return [
                    "top-left",
                    "top-center",
                    "top-right",
                    "middle-left",
                    "center",
                    "middle-right",
                    "bottom-left",
                    "bottom-center",
                    "bottom-right"
                ].includes(val);
            }
        },
        offset: {
            type: Array,
            default: () => [0, 0]
        },
        visible: {
            type: Boolean,
            default: true
        },
        zIndex: {
            type: Number,
            default: 999
        },
        clickable: {
            type: Boolean,
            default: true
        },
        bubble: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        position: {
            handler(val) {
                if(this.$tdtComponent && val?.length >= 2) {
                    this.$tdtComponent.setLngLat(val);
                }
            },
            deep: true
        },
        visible(val) {
            if(this.$tdtComponent) {
                val ? this.$tdtComponent.show() : this.$tdtComponent.hide();
            }
        },
        offset: {
            handler() {
                this.$tdtComponent?.update();
            },
            deep: true
        },
        anchor() {
            this.$tdtComponent?.update();
        },
        clickable(val) {
            const div = this.$tdtComponent?.getElement();
            if(div) {
                div.style.pointerEvents = val ? "auto" : "none";
            }
        }
    },
    updated() {
        // slot 内容更新后只需重算位置（尺寸可能改变），无需任何 DOM 替换
        this.$tdtComponent?.update();
    },
    render(h) {
        if(this.$slots.default) {
            // 外层（$el）保持 display:none 作为占位符，Vue 对外层 patch 时不影响内层
            // 内层（slotContent）不设任何 display style，移入 overlay 后 Vue patch 不会覆盖其可见性
            return h("div", { style: { display: "none" }}, [
                h("div", { ref: "slotContent" }, this.$slots.default)
            ]);
        }
        return h();
    },
    methods: {
        initComponent() {
            const self = this;
            if(!this.position || this.position.length < 2) return;

            const lnglat = new T.LngLat(this.position[0], this.position[1]);

            const anchorMap = {
                "top-left": ["left", "top"],
                "top-center": ["center", "top"],
                "top-right": ["right", "top"],
                "middle-left": ["left", "center"],
                center: ["center", "center"],
                "middle-right": ["right", "center"],
                "bottom-left": ["left", "bottom"],
                "bottom-center": ["center", "bottom"],
                "bottom-right": ["right", "bottom"]
            };

            const CustomOverlay = T.Overlay.extend({
                initialize(lnglat, options) {
                    this.lnglat = lnglat;
                    this.setOptions(options);
                    // 独立跟踪两种可见性状态
                    this._propVisible = self.visible; // prop visible 控制
                    this._inBounds = true; // 地图可视范围控制
                },

                getType() {
                    return 2;
                },

                onAdd(map) {
                    this._map = map;
                    const div = (this._div = document.createElement("div"));
                    div.className = "tdt-slot-marker";
                    div.style.position = "absolute";
                    div.style.zIndex = self.zIndex;
                    div.style.pointerEvents = self.clickable ? "auto" : "none";

                    // ✅ 直接移动内层真实 DOM 节点（非克隆），Vue 仍持续管理其响应式更新
                    // 内层 vnode 不含 display style，Vue patch 不会覆盖 overlay 中的可见性
                    if(self.$refs.slotContent) {
                        div.appendChild(self.$refs.slotContent);
                    }

                    this._bindEvents(div);
                    this._bindMapEvents(map);
                    map.getPanes().markerPane.appendChild(div);
                    this.update();
                    return div;
                },

                onRemove() {
                    this._unbindEvents(this._div);
                    this._unbindMapEvents(this._map);

                    const parent = this._div?.parentNode;
                    if(parent) {
                        parent.removeChild(this._div);
                    }
                    this._map = null;
                    this._div = null;
                },

                _bindEvents(div) {
                    this._onClick = (e) => {
                        if(!self.bubble) e.stopPropagation();
                        self.$emit("click", { ...e, extData: self.extData });
                    };
                    this._onRightClick = (e) => {
                        if(!self.bubble) e.stopPropagation();
                        e.preventDefault();
                        self.$emit("rightclick", {
                            ...e,
                            extData: self.extData
                        });
                    };
                    this._onDblClick = (e) => {
                        if(!self.bubble) e.stopPropagation();
                        self.$emit("dblclick", { ...e, extData: self.extData });
                    };
                    this._onMouseOver = (e) => {
                        if(!self.bubble) e.stopPropagation();
                        self.$emit("mouseover", {
                            ...e,
                            extData: self.extData
                        });
                    };
                    this._onMouseOut = (e) => {
                        if(!self.bubble) e.stopPropagation();
                        self.$emit("mouseout", { ...e, extData: self.extData });
                    };

                    div.addEventListener("click", this._onClick);
                    div.addEventListener("contextmenu", this._onRightClick);
                    div.addEventListener("dblclick", this._onDblClick);
                    div.addEventListener("mouseover", this._onMouseOver);
                    div.addEventListener("mouseout", this._onMouseOut);
                },

                _unbindEvents(div) {
                    if(!div) return;
                    div.removeEventListener("click", this._onClick);
                    div.removeEventListener("contextmenu", this._onRightClick);
                    div.removeEventListener("dblclick", this._onDblClick);
                    div.removeEventListener("mouseover", this._onMouseOver);
                    div.removeEventListener("mouseout", this._onMouseOut);
                },

                // ✅ 监听地图移动 / 缩放，检查是否在可视范围内
                _bindMapEvents(map) {
                    this._onMapChange = () => this._checkBounds();
                    map.addEventListener("move", this._onMapChange);
                    map.addEventListener("moveend", this._onMapChange);
                    map.addEventListener("zoomend", this._onMapChange);
                },

                _unbindMapEvents(map) {
                    if(!map) return;
                    map.removeEventListener("move", this._onMapChange);
                    map.removeEventListener("moveend", this._onMapChange);
                    map.removeEventListener("zoomend", this._onMapChange);
                },

                // ✅ 检查经纬度是否在地图当前可视范围内
                _checkBounds() {
                    if(!this._map || !this.lnglat) return;
                    const bounds = this._map.getBounds();
                    const inBounds = !!(bounds && bounds.contains(this.lnglat));
                    if(inBounds !== this._inBounds) {
                        this._inBounds = inBounds;
                        this._applyVisibility();
                    }
                },

                // ✅ 统一的可见性决策：prop visible && 在可视范围内 才显示
                _applyVisibility() {
                    if(!this._div) return;
                    this._div.style.display
                        = this._propVisible && this._inBounds ? "" : "none";
                },

                show() {
                    this._propVisible = true;
                    this._applyVisibility();
                },

                hide() {
                    this._propVisible = false;
                    this._applyVisibility();
                },

                update() {
                    if(!this._map || !this._div || !this.lnglat) return;

                    // 每次 update 同步检查可视范围
                    this._checkBounds();

                    const pos = this._map.lngLatToLayerPoint(this.lnglat);
                    const [offsetX, offsetY] = self.offset;
                    const [anchorX, anchorY] = anchorMap[self.anchor] || [
                        "center",
                        "bottom"
                    ];

                    const divWidth = this._div.offsetWidth;
                    const divHeight = this._div.offsetHeight;

                    let transformX = 0;
                    let transformY = 0;

                    switch (anchorX) {
                        case "left":
                            transformX = 0;
                            break;
                        case "center":
                            transformX = -divWidth / 2;
                            break;
                        case "right":
                            transformX = -divWidth;
                            break;
                    }

                    switch (anchorY) {
                        case "top":
                            transformY = 0;
                            break;
                        case "center":
                            transformY = -divHeight / 2;
                            break;
                        case "bottom":
                            transformY = -divHeight;
                            break;
                    }

                    this._div.style.left = pos.x + offsetX + transformX + "px";
                    this._div.style.top = pos.y + offsetY + transformY + "px";
                },

                setLngLat(position) {
                    if(position?.length >= 2) {
                        this.lnglat = new T.LngLat(position[0], position[1]);
                        this.update();
                    }
                },

                getElement() {
                    return this._div;
                }
            });

            this.$tdtComponent = new CustomOverlay(lnglat, {});
        }
    }
};
</script>

<style>
.tdt-slot-marker {
    pointer-events: auto;
}
</style>
